import React, {PureComponent} from 'react';
import I18n from 'app/i18n';
import {shopItemPrice, isUSD} from 'app/utils/ShopItemPriceFormat';
import {fixed2} from 'app/utils/fixed';
import {isGuest, slowConnectionPromise} from 'app/utils';
import {showPopup} from 'app/utils/modals/ModalsService';
import logEvent from 'app/amplitude/log_event';
import {confirmGoToDeposits} from 'app/utils/app_links';
import ShopConfirmPopup from 'app/components/shop/shop_confirm_popup';
import {SignInButton} from 'app/components/auth/SignInButton';
import Post from 'app/utils/Post';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {shopLootboxStore} from 'app/store/global';
import {DELAY_BEFORE_OPENING_LOOTBOX} from 'app/store/ArtifactStore';

export interface BuyButtonUser {
    money?: string | number;
    points?: number | string;
}

export interface BuyButtonProps {
    id: number;
    title: string;
    cost_money: string | number;
    cost_points: string | number;
    currency?: string;
    type_shop?: string;
    user?: BuyButtonUser;
    afterBuy?: () => void;
    with_confirmation?: boolean;
    show_loading?: boolean;
    hidePrice?: boolean;
    className?: string;
    disabled?: boolean;
    buyNow?: boolean;
    lootboxOpeningProcess?: boolean;
    customText?: string;
}

interface ShopItemPriceProps {
    cost_money: number;
    cost_points: number;
    currency: string;
}

export default class BuyButton extends PureComponent<BuyButtonProps> {
    componentDidMount(): void {
        this.buyNow();
    }

    componentDidUpdate(): void {
        this.buyNow();
    }

    buyNow = (): void => {
        if (this.props.buyNow) {
            setTimeout(this.buyAction, DELAY_BEFORE_OPENING_LOOTBOX);
            shopLootboxStore.updateBuyAndOpenNowStatus(false);
        }
    };

    get isEnoughBalance(): boolean {
        const {user, cost_points, currency} = this.props;
        const points_price = Number(cost_points);

        if (!user) {
            return true;
        }

        if (!isUSD(currency || '') && points_price > 0) {
            return Number(user.points) - points_price >= 0;
        }
        return true;
    }

    get shopItemPriceProps(): ShopItemPriceProps {
        const {user, cost_points, cost_money, currency = ''} = this.props;
        const userMoney = Number(user?.money || 0);
        const userPoints = Number(user?.points || 0);

        return {
            cost_money: Number(fixed2(Number(cost_money) - userMoney)),
            cost_points: Math.ceil(Number(cost_points) - userPoints),
            currency
        };
    }

    handleBuyAction = (): void => {
        const {title, type_shop, lootboxOpeningProcess, with_confirmation, hidePrice} = this.props;
        const {cost_points} = this.shopItemPriceProps;

        if (lootboxOpeningProcess) {
            return;
        }

        if (type_shop === 'lootbox' && !with_confirmation) {
            this.buyAction();
            return;
        }

        if (!this.isEnoughBalance && hidePrice) {
            toastError(I18n.t('shop.not_enough_points', {count: cost_points}));
            return;
        }

        showPopup(
            'shop-confirm-popup',
            props => <ShopConfirmPopup
                title={title}
                popupId={props.popupId}
                price={shopItemPrice(this.shopItemPriceForDisplay)}
                confirmed={this.buyAction}
            />
        );
    };

    get shopItemPriceForDisplay(): ShopItemPriceProps {
        const {cost_money, cost_points, currency = ''} = this.props;

        return {
            cost_money: Number(cost_money),
            cost_points: Number(cost_points),
            currency
        };
    }

    buyAction = (): void => {
        const {id, title, cost_money, cost_points, type_shop, afterBuy, currency, show_loading} = this.props;

        slowConnectionPromise(
            new Post({
                params: {
                    currency,
                    id
                },
                url: '/shop_items'
            })
                .execute()
                .then((response: Response) => {
                    const unprocessableEntity = 422;

                    if (response.ok) {
                        logEvent(
                            'SHOP_ITEM_PURCHASED',
                            {
                                item: title,
                                price: Number(cost_money) || cost_points,
                                real_money: Boolean(Number(cost_money)),
                                type: type_shop
                            }
                        );
                        toastSuccess(I18n.t('purchase_was_successful_message'));
                        afterBuy && afterBuy();
                    } else if (response.status === unprocessableEntity) {
                        response.json().then((responseJSON: {error: string}) => {
                            const {error} = responseJSON;

                            if (error === I18n.t('insufficient_funds_error')) {
                                confirmGoToDeposits();
                            } else {
                                toastError(error);
                            }
                        });
                    }
                }),
            show_loading
        );
    };

    get customTextButton(): string {
        const {customText} = this.props;

        return customText ? customText : I18n.t('buy_shop_item');
    }

    get buttonClassName(): string {
        const {lootboxOpeningProcess, className} = this.props;

        if (className) {
            return `button-adaptive ${className}${lootboxOpeningProcess || !this.isEnoughBalance ? ' button-adaptive_disable' : ''}`;
        }

        return `button-adaptive ${lootboxOpeningProcess ? 'button-adaptive_disable' : 'btn-open'}`;
    }

    get buttonLabel(): string {
        const {hidePrice} = this.props;

        if (hidePrice) {
            return this.customTextButton;
        }

        return `${this.customTextButton} ${shopItemPrice(this.shopItemPriceForDisplay)}`;
    }

    get insufficientClassName(): string {
        const {className} = this.props;

        if (!className) {
            return 'button-adaptive button-adaptive_disable';
        }

        return `button-adaptive button-adaptive_disable ${className}`;
    }

    render(): React.ReactNode {
        const {disabled, className, hidePrice} = this.props;

        if (isGuest()) {
            return <SignInButton
                title={I18n.t('buy_shop_item')}
                usingOnTab={'SHOP'}
                className={className ? `button-adaptive ${className}` : ''}
            />;
        }
        return this.isEnoughBalance || hidePrice
            ? <button
                className={this.buttonClassName}
                onClick={this.handleBuyAction}
                disabled={disabled}>
                {this.buttonLabel}
            </button>
            : <button className={this.insufficientClassName}>
                {I18n.t('shop.you_need', {amount: shopItemPrice(this.shopItemPriceProps)})}
            </button>;
    }
}
